import { ChangeDetectorRef, Component, HostBinding, HostListener, inject, OnDestroy, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { CanonicalUrlService } from 'projects/shared/lib/services/canonical-url.service';
import { DialogBaseService } from 'projects/shared/lib/services/ui/dialog.service';
import { Subscription } from 'rxjs';
import { EMediaBreakpoint } from 'projects/shared/lib/enum/media/media-breakpoint.enum';
import { ViewportScroller } from '@angular/common';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { IContextService, IContextServiceToken } from 'projects/shared/lib/services/infrastructure/context/context-service.interface';
import { IMediaService, IMediaServiceToken } from 'shared/services/ui/media/media-service.interface';
import { ISidebarCartService, ISidebarCartServiceToken } from 'shared/services/ui/sidebar/cart/sidebar-cart-service.interface';
import { IOverlayService, IOverlayServiceToken } from 'shared/services/ui/overlay/overlay-service.interface';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly contextService: IContextService = inject(IContextServiceToken);
  private readonly mediaService: IMediaService = inject(IMediaServiceToken);
  private readonly sidebarCartService: ISidebarCartService = inject(ISidebarCartServiceToken);
  private readonly overlayService: IOverlayService = inject(IOverlayServiceToken);

  @HostBinding('class') classRoot: string = '';
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {

    // ServiceLocator.injector.get(LoggerService).log(`window.innerWidth: ${window.innerWidth}`);
    // ServiceLocator.injector.get(LoggerService).log(`window.outerWidth: ${window.outerWidth}`);
    // culculation bug existing: 'window.innerWidth' is not updating between ~1000px and 1400px once the width reached 1400px or higher;
    // 'window.outerWidth' is updating without the bug;
    this.mediaService.mediaUpdate(window.outerWidth);
  }

  private contextSubscription?: Subscription | undefined;
  private overlaySubscription: Subscription | undefined;
  private mediaSubscription: Subscription | undefined;
  private dialogIsOpenSubscription: Subscription | undefined;

  public showOverlay?: boolean | undefined;

  public emediaBreakpoint: typeof EMediaBreakpoint;
  public mediaBreakpoint: EMediaBreakpoint;

  private sidebarShoppingCartVisibilitySubscription: Subscription | undefined;
  public shouldDisplaySidebarShoppingCart: boolean | undefined;

  constructor(
    private router: Router,
    private metaTagService: Meta,
    private canonicalUrlService: CanonicalUrlService,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private dialogBaseService: DialogBaseService, // Required for the constructor call of this service -> initialization!
    private viewportScroller: ViewportScroller,
  ) {
    // https://docs.angular.lat/api/router/ExtraOptions#scrollPositionRestoration

    this.metaTagsInit();
    this.canonicalUrlService.setCanonicalURL();
    DialogBaseService.rootViewContainerRefSet(viewContainerRef);
    this.emediaBreakpoint = EMediaBreakpoint;
    this.mediaBreakpoint = this.mediaService.mediaBreakpointGet();
  }

  public ngOnInit(): void {
    this.contextSubscriptionInit();
    this.overlaySubscriptionInit();
    this.mediaSubscriptionInit();
    this.mediaService.mediaUpdate(window.innerWidth);
    this.dialogIsOpenSubscriptionInit();
    this.sidebarShoppingCartVisibilitySubscriptionInit();
    this.loadScroll();
  }

  public ngOnDestroy(): void {
    this.contextSubscription?.unsubscribe();
    this.overlaySubscription?.unsubscribe();
    this.mediaSubscription?.unsubscribe();
    this.dialogIsOpenSubscription?.unsubscribe();
    this.sidebarShoppingCartVisibilitySubscription?.unsubscribe();
  }

  private loadScroll(): void {
    this.router.events.subscribe(
      (event) => {
        let isSafari = this.requirescrollFix();
        if (event instanceof NavigationStart) {
          if (event.navigationTrigger !== 'popstate') {
            if (isSafari) {
              window.scrollTo(0, 0);
            }
            else {
              this.viewportScroller.scrollToPosition([0, 0]);
            }
          }
        }
      }
    );
  }

  private requirescrollFix() {
    try {
      let isSafari = navigator.userAgent.indexOf("Safari") > -1;
      // Chrome has Safari in the user agent so we need to filter (https://stackoverflow.com/a/7768006/1502448)
      let isChrome = navigator.userAgent.indexOf('Chrome') > -1;
      if ((isChrome) && (isSafari)) {
        isSafari = false;
      }

      if (isSafari) {
        return true;
      }
      else {
        return false;
      }

    } catch (e) {
      LoggerService.error(e, this.constructor.name);
      return false;
    }
  }

  private sidebarShoppingCartVisibilitySubscriptionInit() {
    if (!this.sidebarShoppingCartVisibilitySubscription) {
      this.sidebarShoppingCartVisibilitySubscription = this.sidebarCartService.sidebarShoppingCartChangedGet().subscribe(
        (sidebarShoppingCartDesiredVisibility) => {
          this.shouldDisplaySidebarShoppingCart = sidebarShoppingCartDesiredVisibility;
        }
      );
    }
  }

  private dialogIsOpenSubscriptionInit() {
    if (this.dialogIsOpenSubscription == undefined) {
      this.dialogIsOpenSubscription = DialogBaseService.dialogIsOpenSubscription.subscribe(
        (dialogIsOpen) => {
          if (dialogIsOpen) {
            this.renderer.addClass(document.body, 'scrollDisabled');
          } else {
            this.renderer.removeClass(document.body, 'scrollDisabled');
          }
        }
      );
    }
  }

  contextSubscriptionInit() {
    if (!this.contextSubscription) {
      this.contextSubscription = this.contextService.contextChangeGet().subscribe(
        (classRootContext) => {
          this.classRoot = classRootContext;
        }
      );
    }
  }

  overlaySubscriptionInit() {
    if (!this.overlaySubscription) {
      this.overlaySubscription = this.overlayService.isOverlayVisibleChangeGet().subscribe(
        (isOverlayVisible: boolean | undefined) => this.showOverlay = isOverlayVisible
      );
    }
  }

  private mediaSubscriptionInit() {
    if (!this.mediaSubscription) {
      this.mediaSubscription = this.mediaService.mediaBreakpointChangeGet().subscribe(
        (mediaBreakpoint) => {
          // ServiceLocator.injector.get(LoggerService).log(`received breakpoint: ${EMediaBreakpoint[mediaBreakpoint]}`);
          this.mediaBreakpoint = mediaBreakpoint;
        }
      );
    }
  }

  private metaTagsInit() {
    this.metaTagService.addTags([
      { name: 'author', content: 'ORGA-Soft Backnang GmbH' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'date', content: '2022-02-01', scheme: 'YYYY-MM-DD' },
      { charset: 'UTF-8' }
    ]);
  }
}

